import { useMemo, useCallback } from 'react';

import { useCascaderState } from '../context';
import type {
  CascaderOption,
  CascaderOptionGroup,
  CascaderSubmenu,
} from '../types';
import { isSubmenu } from '../util';

// Build the menu stack for each potential level
export type StackItem = {
  groups: CascaderOptionGroup[];
  level: number;
  index: number;
  isOpen: boolean;
  submenu: CascaderSubmenu;
};

/**
 * useMenuStack is responsible for generating the submenus for the CascaderMenu.
 * It generates each level of the menu stack based on the cursor path, and then
 * generates additional submenus for other options at the current level.
 */
export const useMenuStack = <OptionType extends CascaderOption>(
  rootGroups: CascaderOptionGroup<OptionType>[],
  root: CascaderSubmenu<OptionType>,
) => {
  const { cursorPath, getOptionsAtLevel } = useCascaderState();

  // Generate the main menu stack based on cursor path
  const generateMainMenuStack = useCallback(() => {
    const stack: StackItem[] = [];

    // Always add the root level (first submenu)
    stack.push({
      groups: rootGroups,
      level: 0,
      index: 0,
      isOpen: true, // First submenu is always open
      submenu: root,
    });

    // Add levels based on cursor path
    for (let level = 0; level < cursorPath.length; level++) {
      const currentIndex = cursorPath[level];
      const currentOptions = getOptionsAtLevel(level);

      if (currentIndex >= 0 && currentIndex < currentOptions.length) {
        const currentOption = currentOptions[currentIndex];

        if (isSubmenu(currentOption)) {
          stack.push({
            submenu: currentOption,
            groups: currentOption.children as CascaderOptionGroup<OptionType>[],
            level: level + 1,
            index: currentIndex,
            isOpen: true, // Subme  nus in cursor path are open
          });
        } else {
          // Reached a leaf option, stop here
          break;
        }
      } else {
        // Invalid cursor path, stop here
        break;
      }
    }

    return stack;
  }, [root, rootGroups, cursorPath, getOptionsAtLevel]);

  // Generate additional submenus for other options at the current level
  const generateAdditionalSubmenus = useCallback(
    (mainStack: StackItem[]): StackItem[] => {
      if (mainStack.length === 0) return [];

      const lastStackItem = mainStack[mainStack.length - 1];
      const lastLevel = lastStackItem.level;
      const lastGroups = lastStackItem.groups;
      const allOptions = lastGroups.flatMap((group) => group.children);

      return allOptions.reduce<StackItem[]>((acc, option, index) => {
        // Skip if it's not a submenu or if it's the currently selected one
        if (!isSubmenu(option) || cursorPath[lastLevel - 1] === index) {
          return acc;
        }

        return [
          ...acc,
          {
            submenu: option,
            groups: option.children as CascaderOptionGroup<OptionType>[],
            level: lastLevel + 1,
            index,
            isOpen: false, // Additional submenus are closed
          },
        ];
      }, []);
    },
    [cursorPath],
  );

  // Build the complete menu stack
  const menuStack = useMemo(() => {
    const mainStack = generateMainMenuStack();
    const additionalSubmenus = generateAdditionalSubmenus(mainStack);
    return [...mainStack, ...additionalSubmenus];
  }, [generateMainMenuStack, generateAdditionalSubmenus]);

  return menuStack;
};
